import refusal from './letters-signing-refusals-en'

/**
 * Signing certificates (planning P4-3b D-587 to D-589), read as `planning.letters.signing`: Settings → Planning →
 * Signing certificates, the Letters panel's "Sign in gisify" (`panel.*`) and the holder's own list (`mine.*`). The
 * refusal sentences of the certificate routes live in their own file (NEW7).
 */
export default {
  title: 'Signing certificates',
  intro:
    "gisify can sign issued letters with a certificate this council keeps here. What such a signature proves to someone outside gisify: the letter has not changed since it was signed, and it was signed with this council's certificate. Every certificate under the council's root names the same organization as the root: the council's name on its letterhead when the root was made. A reader who has not trusted the council's root certificate sees the signer as unknown. No timestamp or revocation data is included. Someone who broke into gisify could sign letters as the council.",
  root: {
    title: 'Council root certificate',
    create: 'Create the council root',
    help: "Give this certificate to anyone who needs to check your letters. They add it once in Adobe Reader, under Trusted Certificates, for signatures only. It must never be added to a computer's or a browser's root store. Someone who broke into gisify could sign letters in this council's name with it.",
  },
  download: { cer: 'Download (.cer)', pem: 'Download (.pem)' },
  fingerprint: 'SHA-256 fingerprint',
  issue: { title: 'Issue a signing certificate', submit: 'Issue' },
  upload: {
    title: 'Upload a council seal',
    sealOnly:
      'Admins upload council seals here. A personal certificate is uploaded by the person it names, from My signing certificates on the planning list.',
    submit: 'Upload',
  },
  fields: {
    label: 'Label',
    commonName: 'Name on the certificate',
    organization: 'Organisation',
    organizationFixed:
      'Every certificate under this root names {{council}}, the council on the letterhead when the root was made. To change it, delete the root and create a new one.',
    commonNameRootDefault:
      'Leave empty to use the council\'s name followed by "Letter Root", or the name alone if that would be longer than 64 characters. You may type a shorter name.',
    organizationalUnit: 'Unit (optional)',
    country: 'Country code (optional, such as MY)',
    copyFromSignatory: "Copy a signatory's name",
    binding: 'Who may use it',
    person: 'Person',
    format: { pkcs12: 'PKCS#12 file (.p12, .pfx)', pem: 'PEM key and chain' },
    password: 'File password',
    passwordNote:
      'The password is used once to open the file and is then removed from gisify. Copies in database backups expire with the backups.',
    file: 'File',
    keyFile: 'Private key (.pem, .key)',
    chainFile: 'Certificate and chain (.pem, .crt)',
    keyPassword: 'Key password, if the key has one',
  },
  binding: { personal: 'Only this person', seal: 'Any officer who may sign the letter (council seal)' },
  kind: { root: 'Council root', signing: 'Signing certificate' },
  source: { generated: 'Made in gisify', uploaded: 'Uploaded' },
  approve: {
    title: 'Approve a personal certificate',
    selfSigned: 'Self-signed',
    body: "Approve only if the name is this officer's own and the fingerprint matches the one the officer gives you. Once approved, letters signed with this certificate are trusted in gisify and can be shared automatically. Disabling it removes the approval.",
    commonName: "Name compared with the letter's signatory",
    subject: 'Full subject',
    nameNone: 'This certificate has no name gisify can compare.',
    nameMoreThanOne: 'This certificate has more than one name.',
    nameUnclear: 'It cannot be approved: gisify compares exactly one name with the signatory.',
    subjectCut:
      'The full subject is longer than gisify shows. Decide on the name above, not on the end of the subject.',
    hiddenCharacters:
      'The name or subject holds invisible or direction-changing characters, shown here as codes. Do not approve unless you know why they are there.',
  },
  actions: {
    approve: 'Approve',
    disable: 'Disable',
    enable: 'Enable',
    delete: 'Delete',
    cancel: 'Cancel',
    refresh: 'Check again',
  },
  confirm: {
    delete:
      'Delete this certificate? Its private key is removed from gisify and it can never sign again. Copies in database backups expire with the backups. Letters already signed with it are not affected.',
    deleteRoot: 'With the root, every signing certificate it issued is deleted too.',
    disableApproved:
      'Disabling this certificate also removes its approval. If it is enabled again, an admin must approve it again.',
  },
  warning: {
    holderNotQualified:
      'The person this certificate is bound to can no longer sign letters, so letters signed with it are not trusted. Disable or delete it.',
    chainIncomplete:
      'The file did not include the full chain to a root. Readers outside gisify may not be able to check this certificate.',
    icNumber:
      "This certificate carries an IC number. Every letter signed with it needs an officer's confirmation before it is shared.",
  },
  limits:
    'Certificates from a CA on the Adobe Approved Trust List, and Malaysian token or roaming certificates, keep their key on a device and cannot be uploaded. Sign those letters in your own tool and upload the signed file.',
  status: {
    pending: 'Being prepared…',
    pendingSlow: 'This is taking longer than expected.',
    active: 'Ready',
    disabled: 'Disabled',
    deleted: 'Deleted: its key was removed from gisify',
    refused: 'Not accepted',
    expired: 'Expired',
    notYetValid: 'Valid from {{date}}',
    awaitingApproval: 'Ready to sign. Letters wait for an officer until an admin approves it.',
  },
  expires: 'Expires {{date}}',
  expiringSoon: 'Expires in {{days}} days',
  folded: 'Deleted and not accepted ({{count}})',
  problems: {
    required: 'Fill this in.',
    tooLong: 'At most {{max}} characters.',
    control: 'Remove the control character.',
    country: 'Use two capital letters, such as MY.',
    fileMissing: 'Choose a file.',
    fileTooLarge: 'The file is larger than {{max}} KiB.',
    person: 'Choose a person.',
  },
  panel: {
    button: 'Sign in gisify',
    title: 'Sign the letter in gisify',
    choose: 'Choose a certificate',
    what: 'gisify signs the issued letter with this certificate, then checks it exactly as it checks an uploaded file.',
    sealNote: 'A council seal does not name the signatory, so the letter will wait for an officer to share it.',
    unapprovedNote:
      'An admin has not approved this certificate yet, so the letter will wait for an officer to share it.',
    sign: 'Sign',
    signedWith: 'Signed in gisify with {{label}} at {{time}}',
    binding: { personal: 'Personal certificate', seal: 'Council seal' },
    refusal: {
      signing_certificate_unusable:
        'gisify did not sign this letter: the certificate chosen could no longer sign. Choose another, or sign the issued file in your own tool and upload it.',
      signing_key_unreadable:
        "gisify did not sign this letter: the certificate's private key could not be opened. An admin must issue or upload the certificate again.",
      signing_failed:
        'gisify could not sign this letter with that certificate. Try again, and tell an administrator if it happens again.',
      unknown: 'gisify could not sign this letter. Try again, or sign the issued file in your own tool and upload it.',
      dispatch_failed: 'gisify could not start signing this letter. Click Sign in gisify again.',
      verify_timeout: 'Signing and checking took too long and was stopped. Click Sign in gisify again.',
    },
  },
  mine: {
    title: 'My signing certificates',
    open: 'My signing certificates',
    intro:
      'Only you can upload and use a personal certificate bound to you. Nobody else, an admin included, can upload it for you. An admin approves it before letters signed with it can be shared automatically.',
    upload: 'Upload my certificate',
    empty: 'You have no signing certificate yet.',
    close: 'Close',
  },
  refusal,
}
